import React from 'react';
import JobCategoryPage from './JobCategoryPage';
import { Stethoscope } from 'lucide-react';

const ClinicalSocialWorkerJobs = () => {
  const contentSections = [
    {
      title: "What is a Clinical Social Worker?",
      content: (
        <p>
          Clinical social workers are licensed mental health professionals who diagnose and treat mental, behavioral, and emotional disorders. Holding the LCSW (Licensed Clinical Social Worker) credential, these practitioners provide psychotherapy, counseling, and clinical assessments across diverse settings including private practice, hospitals, community mental health centers, and healthcare organizations. Clinical social workers combine their understanding of social systems and environmental factors with clinical skills to provide holistic mental health treatment.
        </p>
      )
    },
    {
      title: "Clinical Social Worker Job Responsibilities",
      content: (
        <p>
          Clinical social workers provide a wide range of mental health services, from individual therapy to group facilitation and crisis intervention. Their clinical training allows them to address complex mental health conditions while considering the social context of clients' lives.
        </p>
      ),
      list: [
        "Conduct comprehensive mental health assessments and diagnostic evaluations",
        "Provide individual, couples, family, and group psychotherapy",
        "Develop and implement evidence-based treatment plans",
        "Diagnose mental health disorders using DSM-5 criteria",
        "Provide crisis intervention and safety planning",
        "Coordinate care with psychiatrists, physicians, and other providers",
        "Maintain detailed clinical documentation and progress notes",
        "Supervise LMSW practitioners working toward clinical licensure",
        "Advocate for clients' access to mental health services",
        "Participate in case consultations and peer supervision"
      ]
    },
    {
      title: "Required Qualifications and Licenses",
      content: (
        <p>
          Clinical social work positions require the highest level of social work licensure: the LCSW (Licensed Clinical Social Worker). This credential requires a Master of Social Work (MSW), supervised clinical experience (typically 2-3 years), and passing a clinical licensing examination. The LCSW allows independent clinical practice and is recognized across all states, though specific requirements vary.
        </p>
      ),
      list: [
        "Master of Social Work (MSW) from a CSWE-accredited program",
        "LCSW (Licensed Clinical Social Worker) credential",
        "3,000+ hours of supervised clinical experience (varies by state)",
        "Proficiency in evidence-based therapeutic modalities",
        "Strong diagnostic and assessment skills",
        "Knowledge of psychopharmacology and medication management basics",
        "Experience with electronic health records and clinical documentation"
      ]
    },
    {
      title: "Salary Expectations for Clinical Social Workers",
      content: (
        <p>
          Clinical social workers command competitive salaries reflecting their advanced training and independent practice capabilities. Salaries typically range from $55,000 to $90,000+ in agency and organizational settings. Those in private practice can earn significantly more, with experienced clinicians billing $100-200+ per session. Factors affecting salary include geographic location, practice setting, specialty focus, and years of experience. Clinical supervisors and program directors often earn at the higher end of the salary spectrum.
        </p>
      )
    },
    {
      title: "Clinical Specializations and Settings",
      content: (
        <p>
          Clinical social workers practice across diverse settings and can specialize in numerous clinical areas. Common practice settings include private practice, community mental health centers, hospital behavioral health units, outpatient clinics, and employee assistance programs. Specializations include trauma and PTSD treatment, substance abuse counseling, child and adolescent therapy, geriatric mental health, eating disorders, and anxiety/depression treatment. Many clinical social workers develop expertise in specific therapeutic modalities such as CBT, DBT, EMDR, or psychodynamic therapy.
        </p>
      )
    },
    {
      title: "Why Choose Clinical Social Work?",
      content: (
        <p>
          Clinical social work offers the unique opportunity to provide direct mental health treatment while maintaining a holistic, person-in-environment perspective. Unlike other mental health professions, clinical social workers are trained to address both individual psychological needs and systemic factors affecting mental health. The profession offers excellent career flexibility, from agency work to private practice, and the ability to specialize in areas of personal interest. For those passionate about mental health and wanting the independence of clinical practice, clinical social work provides a rewarding and sustainable career path.
        </p>
      )
    }
  ];

  const relatedCategories = [
    { name: "Mental Health Social Worker Jobs", href: "/mental-health-social-worker-jobs" },
    { name: "Hospital Social Worker Jobs", href: "/hospital-social-worker-jobs" },
    { name: "School Social Worker Jobs", href: "/school-social-worker-jobs" },
    { name: "Substance Abuse Social Worker Jobs", href: "/substance-abuse-social-worker-jobs" },
  ];

  return (
    <JobCategoryPage
      category="clinical-social-worker-jobs"
      title="Clinical Social Worker Jobs"
      metaTitle="Clinical Social Worker Jobs | LCSW Positions | INeedASocialWorker"
      metaDescription="Find clinical social worker (LCSW) jobs nationwide. Mental health therapy, counseling, and clinical positions. Private practice and agency opportunities. Apply with SW Express Apply™."
      heroTitle="Clinical Social Worker Jobs"
      heroSubtitle="Advance your clinical career with LCSW positions at mental health agencies, hospitals, private practices, and healthcare organizations."
      searchKeywords={["clinical social worker", "LCSW", "clinical social work", "mental health social worker", "therapist social worker", "psychotherapy social worker"]}
      contentSections={contentSections}
      relatedCategories={relatedCategories}
      iconComponent={Stethoscope}
    />
  );
};

export default ClinicalSocialWorkerJobs;
